import { createSlice } from "@reduxjs/toolkit";
import { getAgsReportList } from "../thunk/mapThunk";

// type TradeArea = {
//   id: string,
//   name: string,
//   type: "trade-area",
//   latlng: {
//     lat: number,
//     lng: number,
//   },
//   radiusSetting: [{
//     id: "0",
//     type: "",
//     order: "0",
//     defaultRadius: "0",
//     defaultRingRadiusColor: "#2EDED2",
//     defaultDriveTimeColor: "#17BA49",
//     defaultRingRadiusBorder: "#000000",
//     defaultDriveTimeBorder: "#000000",
//     defaultBorderWidth: "5",
//     defaultOpacity: "1",
//     isTransparent: false,
//     isBorderTransparent: true,
//   }],
//   selected: boolean,
//   pointer: {
//     color: string,
//     size: number,
//   },
//   polygon: any,
//   polygonCoordinates:[],
// };

// Load from localStorage safely
const getPreferredColorPalette = () => {
  try {
    const data = localStorage.getItem("preferredColorPalette");
    return data ? JSON.parse(data) : [];
  } catch (e) {
    console.error("Failed to load preferredColorPalette", e);
    return null;
  }
};

const initialState = {
  showSatelliteOption: false,
  opacityValue: 1,
  mapStyle: "navigation-day-v1",
  layers: { selectedLayers: [] },
  activeMarker: null,
  tradeAreas: [], // @type TradeArea[]
  agsReportList: [],
  showPoiLoader: false,
  mapZoom: 3,
  isMapSaved: false,
  isMapSaving: false,
  saveMap: false,
  saveAsMap: false,
  agsReportDownload: false,
  showAgsReport: null,
  showNotification: false,
  showPoiSettings: false,
  activeCrimeType: "",
  shadingSettings: {
    legendColorSchemeData: getPreferredColorPalette().legendColorSchemeData,
    opacity: 0.6,
    useGradings: false,
    selectedColorScheme: null,
  },
  activeEnvType: "",
};

const mapSlice = createSlice({
  name: "map",
  initialState: initialState,
  reducers: {
    updateActiveCrimeType: (state, action) => {
      state.activeCrimeType = action.payload;
      state.activeEnvType = null;
    },
    updateActiveEnvType: (state, action) => {
      state.activeEnvType = action.payload;
      state.activeCrimeType = null;
    },
    updateShadingSettings: (state, action) => {
      state.shadingSettings = action.payload;
    },
    changeStyle: (state, action) => {
      state.mapStyle = action.payload;
    },
    addTradeArea: (state, action) => {
      state.tradeAreas.push(action.payload);
    },
    updateTradeAreas: (state, action) => {
      state.tradeAreas = action.payload;
    },
    updateTradeArea: (state, action) => {
      const radiusSetting = action.payload?.value?.radiusSetting ?? null;
      const changeByOrder =
        radiusSetting && "order" in radiusSetting && !("id" in radiusSetting);
      return {
        ...state,
        tradeAreas: state.tradeAreas.map((area) =>
          area.id === action.payload.id
            ? {
                ...area,
                ...action.payload.value,
                radiusSetting: radiusSetting
                  ? area.radiusSetting.map((setting) =>
                      setting[changeByOrder ? "order" : "id"].toString() ===
                      radiusSetting[changeByOrder ? "order" : "id"].toString()
                        ? { ...setting, ...radiusSetting }
                        : setting
                    )
                  : area.radiusSetting,
              }
            : area
        ),
      };
    },
    toggleTradeArea: (state, action) => {
      const found = state.tradeAreas.find((d) => d.id === action.payload);
      found.selected = !found.selected;
    },
    removeTradeArea: (state, action) => {
      state.tradeAreas = state.tradeAreas.filter(
        (d) => d.id !== action.payload
      );
    },
    clearTradeArea: (state) => {
      state.tradeAreas = [];
    },
    addSelectedLayer: (state, action) => {
      state.layers.selectedLayers.push(...action.payload);
    },
    setActiveMarker: (state, action) => {
      state.activeMarker = action.payload;
    },
    setMapZoom: (state, action) => {
      state.mapZoom = action.payload;
    },
    showPoiLoader: (state, action) => {
      state.showPoiLoader = action.payload;
    },
    removeSelectedLayer: (state, action) => {
      state.layers.selectedLayers = state.layers.selectedLayers.filter(
        (d) => !action.payload.includes(d)
      );
    },
    setOpacity: (state, action) => {
      state.opacityValue = action.payload;
    },
    createTradeArea: (state, action) => {
      return {
        ...state,
        tradeAreas: action.payload,
      };
    },
    setSatelliteOption: (state, action) => {
      state.showSatelliteOption = action.payload;
    },
    setMapStyle: (state, action) => {
      state.mapStyle = action.payload;
    },
    setSavedMap: (state, action) => {
      state.isMapSaved = action.payload;
    },
    setSaveMapInstance: (state, action) => {
      state.saveMap = action.payload;
    },
    setSaveAsMapInstance: (state, action) => {
      state.saveAsMap = action.payload;
    },
    setMapSaving: (state, action) => {
      state.isMapSaving = action.payload;
    },
    setShowAgsReport: (state, action) => {
      state.showAgsReport = action.payload;
    },
    downloadAgsReport: (state, action) => {
      state.agsReportDownload = action.payload;
    },
    setNotification: (state, action) => {
      state.showNotification = action.payload;
    },
    setPoiSettings: (state, action) => {
      state.showPoiSettings = action.payload;
    },
    resetMapSettings: (state) => {
      state.layers = { selectedLayers: [] };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAgsReportList.fulfilled, (state, action) => {
        return {
          ...state,
          agsReportList: action.payload,
        };
      })
      .addCase(getAgsReportList.pending, (state, action) => {
        return {
          ...state,
        };
      })
      .addCase(getAgsReportList.rejected, (state, action) => {
        return {
          ...state,
        };
      });
  },
});

export const {
  changeStyle,
  addSelectedLayer,
  removeSelectedLayer,
  addTradeArea,
  toggleTradeArea,
  removeTradeArea,
  setActiveMarker,
  updateTradeAreas,
  updateTradeArea,
  showPoiLoader,
  setOpacity,
  createTradeArea,
  setSatelliteOption,
  setMapStyle,
  setSavedMap,
  setSaveMapInstance,
  setSaveAsMapInstance,
  setMapSaving,
  setShowAgsReport,
  downloadAgsReport,
  clearTradeArea,
  setNotification,
  setPoiSettings,
  resetMapSettings,
  updateActiveCrimeType,
  updateShadingSettings,
  updateActiveEnvType,
} = mapSlice.actions;

export default mapSlice.reducer;
